import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const QCMRating = ({ qcmId, currentRating = null, onRatingChange }) => {
    const { t } = useTranslation();
    const { user, isAdmin } = useAuth();
    const [rating, setRating] = useState(currentRating);
    const [hoveredRating, setHoveredRating] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [adminComment, setAdminComment] = useState('');

    // Mapping des étoiles vers les valeurs numériques
    const starValues = {
        1: 1,   // 1 étoile = 1
        2: 3,   // 2 étoiles = 3
        3: 5,   // 3 étoiles = 5
        4: 7,   // 4 étoiles = 7
        5: 10   // 5 étoiles = 10
    };

    // Récupérer la note actuelle de l'utilisateur
    useEffect(() => {
        if (user && qcmId) {
            fetchUserRating();
        }
    }, [user, qcmId, isAdmin]);

    const fetchUserRating = async () => {
        try {
            const response = await fetch(`/api/qcm/rating/${qcmId}?user_id=${user.id || user.username}`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                if (data.star_count !== null && data.star_count !== undefined) {
                    // Utiliser directement star_count de l'API
                    setRating(parseInt(data.star_count));
                    
                    // Récupérer le commentaire admin s'il existe
                    if (data.admin_comment) {
                        setAdminComment(data.admin_comment);
                    }
                }
            }
        } catch (error) {
            console.error('Erreur lors de la récupération de la note:', error);
        }
    };

    const handleRatingClick = async (starCount) => {
        if (!user || isSubmitting) return;

        const numericValue = starValues[starCount];
        setIsSubmitting(true);

        try {
            // Mise à jour 1: Note de l'utilisateur
            const userRatingResponse = await fetch('/api/qcm/rating', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    qcm_id: qcmId,
                    user_id: user.id || user.username,
                    rating: numericValue,
                    admin_comment: isAdmin ? adminComment : null
                })
            });

            if (userRatingResponse.ok) {
                setRating(starCount);
                
                // Appeler le callback parent
                if (onRatingChange) {
                    onRatingChange(numericValue);
                }
            } else {
                console.error('Erreur lors de la mise à jour de la note');
            }
        } catch (error) {
            console.error('Erreur lors de la mise à jour de la note:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleMouseEnter = (starCount) => {
        setHoveredRating(starCount);
    };

    const handleMouseLeave = () => {
        setHoveredRating(0);
    };

    const renderStar = (starNumber) => {
        const isFilled = rating >= starNumber || hoveredRating >= starNumber;
        const isHovered = hoveredRating === starNumber;
        
        // Couleur des étoiles basée sur la valeur
        let starColor = 'text-yellow-400';
        if (starNumber <= 2) starColor = 'text-red-400';
        else if (starNumber <= 3) starColor = 'text-orange-400';
        else if (starNumber <= 4) starColor = 'text-yellow-400';
        else starColor = 'text-green-400';
        
        return (
            <button
                key={starNumber}
                type="button"
                className={`w-5 h-5 transition-all duration-200 opacity-80 hover:opacity-100 ${
                    isFilled ? starColor : 'text-gray-400 hover:text-gray-300'
                } ${isHovered ? 'scale-110' : 'scale-100'}`}
                onClick={() => handleRatingClick(starNumber)}
                onMouseEnter={() => handleMouseEnter(starNumber)}
                onMouseLeave={handleMouseLeave}
                disabled={isSubmitting}
                title={`${starNumber} étoile${starNumber > 1 ? 's' : ''} (${starValues[starNumber]}/10)`}
            >
                <svg 
                    className="w-full h-full" 
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
            </button>
        );
    };

    if (!user) {
        return (
            <div className="bg-gray-700 rounded-lg p-4">
                <p className="text-gray-400 text-sm">
                    {t('login_required_to_rate') || 'Connexion requise pour noter'}
                </p>
            </div>
        );
    }

    if (!qcmId) {
        return (
            <div className="bg-gray-700 rounded-lg p-4">
                <p className="text-gray-400 text-sm">
                    QCM ID manquant
                </p>
            </div>
        );
    }

    return (
        <div className="bg-gray-700 rounded-lg px-3 py-2">
            <div className="text-center mb-1">
                <h3 className="text-xs text-gray-400 font-bold uppercase tracking-wide">
                    {t('rate_this_qcm')}
                </h3>
                {isAdmin && (
                    <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded mt-1 inline-block">
                        {t('admin_mode')}
                    </span>
                )}
            </div>
            
            <div className="flex items-center justify-center space-x-1 mb-2">
                {[1, 2, 3, 4, 5].map(renderStar)}
            </div>
            
            {isAdmin && (
                <div className="mb-1">
                    <textarea
                        className="w-full h-16 bg-gray-600 border border-gray-500 rounded px-2 py-1 text-xs text-white placeholder-gray-400 resize-none"
                        placeholder="Commentaire admin (optionnel)..."
                        rows="2"
                        value={adminComment}
                        onChange={(e) => setAdminComment(e.target.value)}
                    />
                </div>
            )}
            
            {isSubmitting && (
                <div className="mt-3 text-sm text-blue-400">
                    {t('saving_rating')}...
                </div>
            )}
        </div>
    );
};

export default QCMRating;
